<template>
  <div
    class="coupon-item bg-white border rounded-lg shadow-sm flex flex-col"
    :class="{ 'opacity-75': state === 'expired' || state === 'inactive' }"
  >
    <div class="flex items-start justify-between gap-2 p-4 border-b">
      <div class="min-w-0">
        <button
          type="button"
          class="font-mono font-bold text-lg tracking-wide truncate hover:underline"
          title="Copy code"
          @click="copyCode"
        >
          {{ coupon.code }}
        </button>
        <div class="text-2xl font-semibold text-gray-800 leading-tight">
          {{ discountText }}
        </div>
      </div>
      <span
        class="px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
        :class="stateInfo.badge"
      >
        {{ stateInfo.name }}
      </span>
    </div>

    <div class="p-4 flex flex-col gap-2 text-sm text-gray-700 flex-grow">
      <div class="flex justify-between">
        <span class="text-gray-500">Valid</span>
        <span>{{ coupon.fromDate }} → {{ coupon.toDate }}</span>
      </div>
      <div v-if="hasAmountLimits" class="flex justify-between">
        <span class="text-gray-500">Order amount</span>
        <span>{{ amountLimits }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500">Uses</span>
        <span>
          {{ Number(coupon.usedCount) || 0 }}
          / {{ Number(coupon.usesLimit) ? coupon.usesLimit : "∞" }}
          <span v-if="Number(coupon.usesLimitUser)" class="text-gray-500">
            ({{ coupon.usesLimitUser }} per customer)
          </span>
        </span>
      </div>
      <div v-if="coupon.discountType === 'product'" class="flex justify-between">
        <span class="text-gray-500">Applies to</span>
        <button
          type="button"
          class="text-blue-600 hover:underline"
          @click="$emit('edit', coupon, rulesCount ? 'products' : 'categories')"
        >
          <template v-if="rulesCount">
            {{ plural(coupon.productsCount, "product") }},
            {{ plural(coupon.categoriesCount, "category", "categories") }}
          </template>
          <span v-else class="text-red-600 font-semibold">
            Nothing yet — add rules
          </span>
        </button>
      </div>
      <div class="flex flex-wrap gap-1 mt-1">
        <span
          v-if="Number(coupon.freeShipping)"
          class="px-2 py-1 rounded bg-green-50 text-green-800 text-xs"
        >
          Free shipping
        </span>
        <span
          v-if="coupon.conditions"
          class="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs"
          :title="coupon.conditions"
        >
          {{ coupon.conditions }}
        </span>
      </div>
    </div>

    <div class="flex items-center justify-end gap-2 px-4 py-3 border-t bg-gray-50">
      <template v-if="confirmDelete">
        <span class="text-sm text-gray-700 mr-auto">Delete this coupon?</span>
        <button class="btn btn-error-button" :disabled="deleting" @click="deleteC">
          {{ deleting ? "Deleting..." : "Delete" }}
        </button>
        <button class="btn btn-dark-button" @click="confirmDelete = false">
          Cancel
        </button>
      </template>
      <template v-else>
        <button class="w-6 h-6" title="Delete" @click="confirmDelete = true">
          <img src="@/assets/icons/trash.svg" alt="delete" />
        </button>
        <button class="btn btn-dark-button" @click="$emit('edit', coupon)">
          Edit
        </button>
      </template>
    </div>
  </div>
</template>
<script>
import { deleteCoupon as deleteCouponApi } from "@/api/store/CouponsService.js";
import {
  couponDiscountText,
  couponState,
  COUPON_STATES
} from "./couponLabels.js";

export default {
  name: "CouponItem",
  props: {
    coupon: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      confirmDelete: false,
      deleting: false
    };
  },
  computed: {
    state() {
      return couponState(this.coupon);
    },
    stateInfo() {
      return COUPON_STATES[this.state];
    },
    discountText() {
      return couponDiscountText(this.coupon);
    },
    rulesCount() {
      return (
        Number(this.coupon.productsCount) + Number(this.coupon.categoriesCount)
      );
    },
    hasAmountLimits() {
      return Number(this.coupon.minAmount) || Number(this.coupon.maxAmount);
    },
    amountLimits() {
      const min = Number(this.coupon.minAmount);
      const max = Number(this.coupon.maxAmount);
      if (min && max) return `$${min} – $${max}`;
      if (min) return `from $${min}`;
      return `up to $${max}`;
    }
  },
  methods: {
    plural(count, singular, pluralWord) {
      const n = Number(count) || 0;
      return `${n} ${n === 1 ? singular : pluralWord || singular + "s"}`;
    },
    copyCode() {
      navigator.clipboard.writeText(this.coupon.code).then(() => {
        this.$toast.success(`${this.coupon.code} copied`);
      });
    },
    async deleteC() {
      this.deleting = true;
      try {
        await deleteCouponApi(this.coupon.id);
        this.$toast.success("Coupon deleted");
        this.$emit("deleted");
      } catch (e) {
        this.$toast.error(e.response && e.response.data ? e.response.data : e.message);
      } finally {
        this.deleting = false;
        this.confirmDelete = false;
      }
    }
  }
};
</script>
